import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import KinAvatar from "@/components/KinAvatar";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { afterTicketSuccess, chooseGoogleCallbackPath } from "@/lib/googleConnectUrl";

const GOOGLE_CAL_URL = "https://unnzlorzyswiahjlxwqh.supabase.co/functions/v1/google-calendar-auth";
const ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InVubnpsb3J6eXN3aWFoamx4d3FoIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzYxMDU5NTMsImV4cCI6MjA5MTY4MTk1M30.4zmLgxfgFlg9mnvkqQVPgsmV8bP1UO_OyLb3wXjgSfo";

const isMedian = () =>
  typeof navigator !== "undefined" && /median/i.test(navigator.userAgent);

const isMobileApple = () =>
  typeof navigator !== "undefined" && /iphone|ipod|ipad/i.test(navigator.userAgent);

export default function GoogleCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // #1798 — `success_in_browser` is the ticket path: connected, but we are in a
  // Safari tab with no Kin session, so "redirect to Today" would show a login
  // screen. The honest next step is "go back to the Kin app", and we say so.
  const [status, setStatus] = useState<"loading" | "success" | "success_in_browser" | "error" | "safari_fallback">("loading");
  // TEMP: Remove after Calendar Connect exchange path is verified working.
  const [diag, setDiag] = useState<string | null>(null);

  useEffect(() => {
    const code = searchParams.get("code");
    // #1798 — the one-time connect ticket, echoed back by Google as `state`.
    const state = searchParams.get("state");
    const errorParam = searchParams.get("error");

    if (!code && !errorParam) {
      navigate("/settings");
      return;
    }

    const REDIRECT_URI = `${window.location.origin}/google/callback`;

    // #1798 — the ticket path. Runs in a tab that MAY hold no Kin session (external
    // Safari opened from the Median shell) — no Authorization header on purpose.
    // The apikey is Supabase's project router, not an identity claim, verify_jwt is
    // off for this function (config.toml), authPolicy admits exactly this one
    // action for an anonymous caller, and the handler learns the user from the
    // ticket row it consumes.
    const ticketExchange = async (ticket: string, hasSession: boolean) => {
      try {
        const resp = await fetch(GOOGLE_CAL_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json", apikey: ANON_KEY },
          body: JSON.stringify({ action: "exchange_with_ticket", code, ticket, redirect_uri: REDIRECT_URI }),
        });
        if (!resp.ok) {
          console.warn(`[GoogleCallback] exchange_with_ticket ${resp.status}`, await resp.text().catch(() => ""));
          setStatus("error");
          return;
        }
        // Copilot on #1804: the signed-in iPhone Safari WEB app reaches this
        // path too, and was then told to "head back to the Kin app" with no way
        // home — despite being the app. If this tab has a session, it IS the
        // app: route to Today exactly as the session path does.
        if (afterTicketSuccess(hasSession) === "route_home") {
          setStatus("success");
          setTimeout(() => navigate("/"), 2000);
          return;
        }
        setStatus("success_in_browser");
      } catch (e) {
        console.error("Google Calendar ticket exchange error:", e);
        setStatus("error");
      }
    };

    const sessionExchange = async () => {
      // TEMP: Remove after Calendar Connect exchange path is verified working.
      const collect = async (extra: Record<string, unknown>) => {
        const lines = [
          `code present: ${code ? "yes" : "no"}`,
          code ? `code preview: ${code.slice(0, 12)}... (len=${code.length})` : `code preview: -`,
          `error param: ${errorParam ?? "(none)"}`,
          `redirect_uri used: ${REDIRECT_URI}`,
          `window.location.href: ${typeof window !== "undefined" ? window.location.href : "(n/a)"}`,
          `userAgent: ${typeof navigator !== "undefined" ? navigator.userAgent : "(n/a)"}`,
          ...Object.entries(extra).map(([k, v]) => `${k}: ${typeof v === "string" ? v : JSON.stringify(v)}`),
        ];
        return lines.join("\n");
      };

      try {
        // getUser + getSession here are two of the three sanctioned direct auth
        // calls (CLAUDE.md): this is a one-shot sequential OAuth callback, not a
        // mount-time call competing for the auth lock.
        //
        // The session's access_token is now required (#494): google-calendar-auth
        // used to trust the user_id in this request body while querying with a
        // service-role client, and this call sent the PUBLIC anon key — so the
        // exchange endpoint was reachable by anyone. The function now derives the
        // acting user from the token.
        const { data: { user }, error: userErr } = await supabase.auth.getUser();
        const { data: { session: authSession } } = await supabase.auth.getSession();
        const accessToken = authSession?.access_token;

        if (!user || !accessToken) {
          if (isMedian()) {
            setDiag(await collect({
              "supabase user": user ? `${user.id.slice(0, 8)}... (no access token)` : "none",
              "supabase getUser error": userErr?.message ?? "(no error, just null user)",
              "stage": "before exchange — no session",
            }));
          }
          setStatus("error");
          return;
        }

        if (errorParam) {
          if (isMedian()) {
            setDiag(await collect({
              "supabase user": `${user.id.slice(0, 8)}...`,
              "stage": "google returned error= instead of code=",
            }));
          }
          setStatus("error");
          return;
        }

        let resp: Response;
        try {
          resp = await fetch(GOOGLE_CAL_URL, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
              apikey: ANON_KEY,
            },
            body: JSON.stringify({ action: "exchange", code, user_id: user.id, redirect_uri: `${window.location.origin}/google/callback` }),
          });
        } catch (fetchErr) {
          if (isMedian()) {
            setDiag(await collect({
              "supabase user": `${user.id.slice(0, 8)}...`,
              "stage": "fetch threw before response",
              "fetch error": (fetchErr as Error)?.message ?? String(fetchErr),
            }));
          }
          setStatus("error");
          return;
        }

        const bodyText = await resp.text().catch(() => "(failed to read body)");

        if (!resp.ok) {
          if (isMedian()) {
            setDiag(await collect({
              "supabase user": `${user.id.slice(0, 8)}...`,
              "stage": "edge function non-2xx",
              "http status": resp.status,
              "response body": bodyText.slice(0, 500),
            }));
          }
          setStatus("error");
          return;
        }

        setStatus("success");
        setTimeout(() => navigate("/"), 2000);
      } catch (e) {
        console.error("Google Calendar auth callback error:", e);
        if (isMedian()) {
          setDiag(await collect({
            "stage": "unexpected throw",
            "error": (e as Error)?.message ?? String(e),
          }));
        }
        setStatus("error");
      }
    };

    // #1798 — which way can this page finish? iOS never fires a Universal Link
    // for Google's redirect INTO our domain (#626), so from the Median app this
    // page renders in external Safari with no Kin session. It used to give up
    // here with a "Tap to return to Kin" button whose handler was a same-URL
    // reload — which cannot fire one either. Now, if the app minted a ticket,
    // the page can finish the exchange itself: the ticket names the user.
    //
    // getSession first: it tells the iPhone Safari WEB app (signed in, same
    // tab) apart from external Safari (not signed in). On the ticket path it
    // only decides where she goes afterwards — the TICKET always decides whose
    // account the tokens attach to. Sanctioned direct auth call here, as the
    // existing getUser/getSession below are: a one-shot callback, not a
    // mount-time read competing for the auth lock.
    (async () => {
      let hasSession = false;
      try {
        const { data: { session: tabSession } } = await supabase.auth.getSession();
        hasSession = !!tabSession?.access_token;
      } catch {
        hasSession = false; // unreadable → treat as not signed in here
      }

      const path = chooseGoogleCallbackPath({
        hasCode: !!code,
        hasState: !!state,
        isMedian: isMedian(),
        isMobileApple: isMobileApple(),
        hasSession,
      });

      if (path === "safari_fallback") {
        // A link minted before #1798 shipped, in a tab with no session — nothing
        // this page can do but say so.
        setStatus("safari_fallback");
        return;
      }
      if (path === "exchange_with_ticket" && state) {
        await ticketExchange(state, hasSession);
        return;
      }
      await sessionExchange();
    })();
  }, [searchParams, navigate]);

  // TEMP: Remove after Calendar Connect exchange path is verified working.
  const handleCopyDiag = async () => {
    if (!diag) return;
    try {
      await navigator.clipboard.writeText(diag);
      toast.success("Copied to clipboard");
    } catch {
      toast("Clipboard blocked — please select the text manually");
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: "#F0F0FF" }}>
      <div className="text-center px-6 max-w-sm w-full">
        {status === "loading" && (
          <>
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-t-transparent mx-auto mb-3" style={{ borderColor: "#5050C8", borderTopColor: "transparent" }} />
            <p className="text-sm font-body" style={{ color: "#54525C" }}>Connecting Google Calendar...</p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="flex justify-center mb-3">
              <KinAvatar size="md" variant="dark" />
            </div>
            <p className="text-base font-body font-semibold" style={{ color: "#5050C8" }}>
              Google Calendar connected! ✅
            </p>
            <p className="text-xs font-body mt-1" style={{ color: "#54525C" }}>
              Redirecting to Today...
            </p>
          </>
        )}
        {status === "success_in_browser" && (
          <>
            <div className="flex justify-center mb-4">
              <KinAvatar size="md" variant="dark" />
            </div>
            <h1 className="font-display mb-2" style={{ fontSize: "24px", color: "#1C1A3E" }}>
              You're connected 💜
            </h1>
            <p className="font-body" style={{ fontSize: "14px", color: "#54525C", lineHeight: 1.5 }}>
              Google Calendar is linked to Kin. Head back to the Kin app — your events will be waiting on Today.
            </p>
          </>
        )}
        {status === "safari_fallback" && (
          <>
            {/* #1798 — reachable only from a link minted before the ticket
                existed. The old "Open in Kin" button here reloaded this same
                URL, which iOS will never resolve as a Universal Link, so it
                could not do anything. Say what is true instead. */}
            <div className="flex justify-center mb-4">
              <KinAvatar size="md" variant="dark" />
            </div>
            <h1 className="font-display mb-2" style={{ fontSize: "24px", color: "#1C1A3E" }}>
              This link has expired
            </h1>
            <p className="font-body" style={{ fontSize: "14px", color: "#54525C", lineHeight: 1.5 }}>
              Open the Kin app and tap Connect Google Calendar again — it only takes a second.
            </p>
          </>
        )}
        {status === "error" && (
          <>
            <p className="text-sm font-body mb-4" style={{ color: "#54525C" }}>
              Something went wrong connecting Google Calendar. Please try again.
            </p>
            <button
              onClick={() => navigate("/settings")}
              className="h-10 px-6 rounded-xl font-body font-semibold text-sm text-white"
              style={{ backgroundColor: "#5050C8" }}
            >
              Back to Settings
            </button>
          </>
        )}
      </div>

      {/* TEMP: Remove after Calendar Connect exchange path is verified working. */}
      <Dialog open={!!diag} onOpenChange={(open) => { if (!open) setDiag(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Calendar Connect Diagnostic (Temporary)</DialogTitle>
            <DialogDescription>
              Debug info to fix Calendar Connect. Tap "Copy" and send to Kasandra.
            </DialogDescription>
          </DialogHeader>
          <pre
            className="text-xs whitespace-pre-wrap break-all rounded-md p-3 max-h-72 overflow-auto"
            style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", backgroundColor: "#F0F0FF", color: "#1C1A3E" }}
          >
{diag}
          </pre>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDiag(null)}>Close</Button>
            <Button onClick={handleCopyDiag} style={{ backgroundColor: "#5050C8", color: "#FFFFFF" }}>
              Copy to clipboard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
